import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, ClipboardPaste, Landmark, Lock, Mail, RefreshCcw, ShieldCheck, Sparkles, Timer, TrendingUp, Truck,
} from 'lucide-react';
import BrandMark from './BrandMark';
import { isLoggedIn, signOut } from './neveroverpayApi';
import { GmailLogo } from './GmailConnectCard';
import { TrackerStages } from './RecoveryTracker';
import { CountUp, Glow, Reveal, fadeUp, stagger, useTicker } from './npUi';
import './neveroverpay.css';
import './neveroverpay-ui.css';

const LANDING_NAV = [
  { href: '#recoveries', label: 'What we recover' },
  { href: '#how', label: 'How it works' },
  { href: '#money', label: 'Money saved' },
  { href: '#safety', label: 'Safety' },
  { href: '#faq', label: 'Questions' },
];

function LandingNav({ loggedIn, onSignOut }) {
  const [menu, setMenu] = React.useState(false);

  function closeMenu() {
    setMenu(false);
  }

  return (
    <div className="np-nav-bar">
      <nav className="np-wrap np-nav" aria-label="BilledTwice home">
        <BrandMark href="#top" kicker="Get your money back" onClick={closeMenu} />
        <div className={`np-nav-links${menu ? ' open' : ''}`} id="np-home-nav">
          {LANDING_NAV.map((item) => (
            <a key={item.href} href={item.href} onClick={closeMenu}>{item.label}</a>
          ))}
        </div>
        <div className="np-nav-auth">
          {loggedIn ? (
            <>
              <Link to="/app" onClick={closeMenu}>Home</Link>
              <Link className="np-btn" to="/app" onClick={closeMenu}>Open my recoveries</Link>
              <button type="button" className="np-btn ghost" onClick={onSignOut}>Sign out</button>
            </>
          ) : (
            <>
              <Link className="np-btn ghost" to="/auth?mode=login&next=/onboarding" onClick={closeMenu}>Sign in</Link>
              <Link className="np-btn" to="/auth?mode=register&next=/onboarding" onClick={closeMenu}>Sign up</Link>
            </>
          )}
          <button
            type="button"
            className="np-nav-toggle"
            aria-expanded={menu}
            aria-controls="np-home-nav"
            onClick={() => setMenu((on) => !on)}
          >
            {menu ? 'Close' : 'Menu'}
          </button>
        </div>
      </nav>
    </div>
  );
}

const DEMO_CASES = [
  {
    id: 'demo-1',
    title: 'Missing refund from Best Buy',
    merchant: 'Best Buy · order BBY01-8065',
    amount: '$189.99',
    workflow_label: 'Missing refund',
    status: 'active',
    stages: [
      { id: 'found', label: 'Found', done: true },
      { id: 'review', label: 'Proof ready', done: true },
      { id: 'approved', label: 'Approved', done: true },
      { id: 'asked', label: 'Asked the company', current: true },
      { id: 'reply', label: 'Company replied' },
      { id: 'money_back', label: 'Money back in bank' },
    ],
  },
  {
    id: 'demo-2',
    title: 'Stop a renewal at Adobe',
    merchant: 'Adobe · renews in 13 days',
    amount: '$659.88',
    workflow_label: 'Renewal rescue',
    status: 'active',
    stages: [
      { id: 'found', label: 'Found', done: true },
      { id: 'review', label: 'Proof ready', current: true },
      { id: 'approved', label: 'Approved' },
      { id: 'asked', label: 'Asked the company' },
      { id: 'reply', label: 'Company replied' },
      { id: 'money_back', label: 'Money back in bank' },
    ],
  },
  {
    id: 'demo-3',
    title: 'Late delivery credit from Amazon',
    merchant: 'Amazon · arrived 4 days late',
    amount: '$12.99',
    workflow_label: 'Late delivery credit',
    status: 'won',
    stages: [
      { id: 'found', label: 'Found', done: true },
      { id: 'review', label: 'Proof ready', done: true },
      { id: 'approved', label: 'Approved', done: true },
      { id: 'asked', label: 'Asked the company', done: true },
      { id: 'reply', label: 'Company replied', done: true },
      { id: 'money_back', label: 'Money back in bank', done: true },
    ],
  },
];

const PROBLEMS = [
  { id: 'refund', icon: RefreshCcw, tone: '', title: 'Refund never arrived', blurb: 'They said they refunded you, the waiting period passed, and the money still is not there.', stat: 'avg. $86 back' },
  { id: 'renewal', icon: Timer, tone: 'amber', title: 'Stop a renewal charge', blurb: 'A trial is ending or the price is jumping. Cancel or freeze before they charge you.', stat: 'avg. $240 / yr' },
  { id: 'delivery', icon: Truck, tone: 'blue', title: 'Late delivery credit', blurb: 'It arrived later than promised. Ask for the shipping refund or delivery guarantee.', stat: 'avg. $14 back' },
];

const STEPS = [
  { id: 'how-read', title: 'Check your emails', blurb: 'Connect Gmail, paste a company email, or upload a refund, renewal, or delivery note.' },
  { id: 'how-proof', title: 'Show the proof', blurb: 'AI quotes the merchant’s own dates and amounts. Rules decide if you qualify.' },
  { id: 'how-ok', title: 'Get your OK', blurb: 'You see the exact draft. Approving locks that wording. Nothing goes out without it.' },
  { id: 'how-ask', title: 'Ask the company', blurb: 'We send the approved email, or you copy it yourself. Then we watch the reply.' },
  { id: 'how-count', title: 'Count only real money', blurb: 'A matching bank credit — amount, date, merchant — is what moves the headline.' },
];

const BUCKETS = [
  { id: 'money-verified', title: 'Verified recovered', blurb: 'Bank activity confirms the money is back. This is the only headline.', tag: 'headline', tone: 'green' },
  { id: 'money-pending', title: 'Pending', blurb: 'You approved. Waiting on the company or a matching deposit.', tag: 'waiting', tone: 'amber' },
  { id: 'money-potential', title: 'Potential', blurb: 'Found in mail. No action yet. Not counted as saved.', tag: 'not counted', tone: 'blue' },
  { id: 'money-recurring', title: 'Annual recurring', blurb: 'A renewal you stopped. Kept apart from money recovered this year.', tag: 'per year', tone: '' },
];

const SAFETY = [
  { id: 'safety-approve', icon: Lock, title: 'You approve every send', blurb: 'Approval is bound to the exact draft. If the plan changes, you approve again.' },
  { id: 'safety-gmail', icon: ShieldCheck, title: 'Gmail stays read-only first', blurb: 'We look for refunds, renewals, and delivery mail. Sending needs a second permission.' },
  { id: 'safety-bank', icon: Landmark, title: 'Bank match, not guesses', blurb: 'Plaid or a CSV. A credit must match amount, date window, and merchant before we count it.' },
];

const FAQ = [
  ['Do you email companies without asking?', 'No. You approve the exact wording first. That lock is required before any send.'],
  ['When do you count money saved?', 'When a posted bank credit matches the recovery — not when the merchant says they issued it.'],
  ['What mail do you read?', 'Refund, renewal, and delivery messages that look like they could be recovered. Irrelevant mail is skipped.'],
  ['Can I use this without Gmail or Plaid?', 'Yes. Paste or upload a company email, then upload a bank CSV to verify the deposit.'],
  ['What is the weekly digest?', 'One friendly email a week, sent from your own Gmail: what came back, what is pending, and what needs your OK.'],
];

function HeroMock() {
  const index = useTicker(DEMO_CASES.length, 3400);
  const item = DEMO_CASES[index];
  return (
    <div className="np-mock" data-testid="landing-hero-mock">
      <div className="np-mock-head">
        <div className="np-product-dots"><span /><span /><span /></div>
        <span>Home · Overview</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}><span className="np-pulse-dot" /> watching Gmail</span>
      </div>
      <div className="np-mock-body">
        <div className="np-mock-stats">
          <div className="np-mock-stat"><span>Verified this year</span><strong className="np-money"><CountUp value={1284.5} prefix="$" /></strong></div>
          <div className="np-mock-stat"><span>Pending</span><strong><CountUp value={849.87} prefix="$" /></strong></div>
          <div className="np-mock-stat"><span>Annual saved</span><strong><CountUp value={659.88} prefix="$" decimals={0} /></strong></div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={item.id}
            className="np-mock-case"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            style={{ gridTemplateColumns: '1fr' }}
          >
            <div className="np-between">
              <div>
                <span className={`np-tag ${item.status === 'won' ? 'green' : 'amber'}`}>{item.status === 'won' ? 'Money back' : item.workflow_label}</span>
                <h4>{item.title}</h4>
                <p>{item.merchant}</p>
              </div>
              <div className="np-tracker-amount np-money">{item.amount}</div>
            </div>
            <TrackerStages stages={item.stages} status={item.status} compact />
          </motion.div>
        </AnimatePresence>
      </div>
      <motion.div className="np-float-chip" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}>
        <CheckCircle2 size={14} /> Bank matched $12.99 · counted
      </motion.div>
    </div>
  );
}

export default function NeverOverpayLanding() {
  const loggedIn = isLoggedIn();
  const next = loggedIn ? '/app' : '/auth?mode=register&next=/onboarding';
  const connectHref = loggedIn ? '/app?connect=gmail' : '/auth?mode=register&next=/onboarding';

  return (
    <div className="np-root" data-testid="neveroverpay-landing" id="top">
      <LandingNav loggedIn={loggedIn} onSignOut={signOut} />
      <div className="np-hero-wrap np-grid-bg">
        <Glow />
        <div className="np-wrap">
          <section className="np-hero-v2">
            <motion.div variants={stagger} initial="hidden" animate="show">
              <motion.div variants={fadeUp} className="np-hero-badge"><span className="dot" /> AI reads the mail · you approve every send · bank confirms the money</motion.div>
              <motion.h1 variants={fadeUp}>Find the money you are owed. <em>Get it back.</em> Count it only when it lands.</motion.h1>
              <motion.p variants={fadeUp} className="lead">
                Connect Gmail or paste a real company email. AI quotes the facts, drafts the request, and tracks every refund from
                “asked” to “money back in bank”. A weekly digest keeps you in the loop.
              </motion.p>
              <motion.div variants={fadeUp} className="np-actions">
                <Link className="np-btn gmail np-btn-lg np-btn-icon" to={connectHref} data-testid="hero-connect-gmail"><GmailLogo size={18} /> Connect Gmail</Link>
                {loggedIn ? (
                  <Link className="np-btn ghost np-btn-lg np-btn-icon" to="/app">Open my recoveries <ArrowRight size={16} /></Link>
                ) : (
                  <Link className="np-btn ghost np-btn-lg" to="/auth?mode=register&next=/onboarding" data-testid="hero-signup">Sign up free</Link>
                )}
                <a className="np-btn ghost np-btn-lg" href="#how">See how it works</a>
              </motion.div>
              <motion.div variants={fadeUp} className="np-hero-proof">
                <div><strong className="np-money"><CountUp value={0} format={(v) => `${Math.round(v)}%`} /></strong><span>counted before the bank confirms</span></div>
                <div><strong><CountUp value={6} decimals={0} /></strong><span>tracked stages, found → money back</span></div>
                <div><strong>1×</strong><span>weekly digest, from your own Gmail</span></div>
                <div><strong>Read-only</strong><span>Gmail until you approve a send</span></div>
              </motion.div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30, rotateX: 6 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
              <HeroMock />
            </motion.div>
          </section>
        </div>
      </div>

      <div className="np-marquee" aria-hidden="true">
        <motion.div className="np-marquee-track" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}>
          {[0, 1].map((copy) => (
            <React.Fragment key={copy}>
              <span className="np-marquee-item"><Mail size={14} /> Refund issued but never arrived → <strong>asked</strong></span>
              <span className="np-marquee-item"><Timer size={14} /> Renewal in 13 days → <strong>cancel before charge</strong></span>
              <span className="np-marquee-item"><Truck size={14} /> Delivered 4 days late → <strong>shipping credit</strong></span>
              <span className="np-marquee-item"><Landmark size={14} /> Bank credit matched → <strong>counted as saved</strong></span>
              <span className="np-marquee-item"><TrendingUp size={14} /> Price increase notice → <strong>freeze the price</strong></span>
              <span className="np-marquee-item"><Sparkles size={14} /> Duplicate subscription → <strong>one canceled</strong></span>
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      <div className="np-wrap">
        <section className="np-section" id="recoveries">
          <Reveal><div className="np-kicker">What we recover</div><h2 className="np-section-title">Three everyday money problems</h2></Reveal>
          <motion.div className="np-grid-3" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            {PROBLEMS.map(({ id, icon: Icon, tone, title, blurb, stat }) => (
              <motion.div variants={fadeUp} className="np-card np-lift" id={id} key={id}>
                <div className={`np-icon-box ${tone}`}><Icon size={18} /></div>
                <h3>{title}</h3>
                <p className="np-muted">{blurb}</p>
                <span className="np-tag" style={{ marginTop: 12 }}>{stat}</span>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <section className="np-section" id="how">
          <Reveal>
            <div className="np-kicker">How it works</div>
            <h2 className="np-section-title">Check your emails → show the proof → get your OK → ask the company → count only real money</h2>
            <p className="np-muted np-section-lead">
              This is not a chatbot that guesses. AI may read the email and draft the wording. A recovery still opens only when the dates
              and amounts qualify. You approve the exact request before anything is sent. “Money saved” only includes money that showed up on a matching bank line.
            </p>
          </Reveal>
          <motion.div className="np-steps-v2" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            {STEPS.map((step, index) => (
              <motion.div variants={fadeUp} className="np-card np-lift np-step-v2" id={step.id} key={step.id}>
                <div className="np-step-num">{String(index + 1).padStart(2, '0')}</div>
                <h3>{step.title}</h3>
                <p className="np-muted">{step.blurb}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <section className="np-section" id="tracker">
          <div className="np-grid-2" style={{ alignItems: 'center', gap: 40 }}>
            <Reveal>
              <div className="np-kicker">Recovery tracker</div>
              <h2 className="np-section-title">Every request, one honest timeline.</h2>
              <p className="np-muted np-section-lead" style={{ marginBottom: 16 }}>
                Found → Proof ready → Approved → Asked the company → Company replied → Money back in bank. You always know where a refund stands, how long it has been open, and what happens next.
              </p>
              <ul className="np-list">
                <li>Live stage per recovery, with dates</li>
                <li>“Needs your OK” surfaces first</li>
                <li>Won vs. closed kept honest — nothing counted until the bank agrees</li>
              </ul>
              <div className="np-actions"><Link className="np-btn np-btn-icon" to={next}>Start tracking <ArrowRight size={15} /></Link></div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="np-tracker-card" style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}>
                <div className="np-tracker-top">
                  <div>
                    <span className="np-tag amber">Asked the company</span>
                    <h3>Missing refund from Best Buy</h3>
                    <p className="np-muted" style={{ margin: 0 }}>Best Buy · open 9 days</p>
                  </div>
                  <div className="np-tracker-amount">$189.99</div>
                </div>
                <TrackerStages stages={DEMO_CASES[0].stages} status="active" />
                <div className="np-tracker-next"><span className="np-muted">Next:</span><strong>Waiting on the company. We nudge them if they go quiet.</strong></div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="np-section" id="digest">
          <div className="np-grid-2" style={{ alignItems: 'center', gap: 40 }}>
            <Reveal className="np-card np-accent" style={{ padding: 24 }}>
              <div className="np-row" style={{ marginBottom: 10 }}><div className="np-icon-box" style={{ marginBottom: 0 }}><Mail size={18} /></div><strong>Your money digest · Monday 9:00</strong></div>
              <h3 style={{ fontSize: 22, letterSpacing: '-0.03em', margin: '8px 0 4px' }}>$202.98 landed back in your bank this week.</h3>
              <p className="np-muted">Week of Aug 30 → Sep 6</p>
              <div className="np-grid-2" style={{ marginTop: 14, gap: 10 }}>
                <div className="np-card np-soft" style={{ padding: 12 }}><div className="np-muted">Back in bank</div><div className="np-stat np-money" style={{ fontSize: 22 }}>$202.98</div></div>
                <div className="np-card np-soft" style={{ padding: 12 }}><div className="np-muted">Waiting on your OK</div><div className="np-stat" style={{ fontSize: 22 }}>$659.88</div></div>
              </div>
              <p className="np-muted" style={{ marginTop: 12 }}>Sent from your own Gmail, to you. Change the day or turn it off any time.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="np-kicker">Weekly money digest</div>
              <h2 className="np-section-title">One friendly email. Never a guess.</h2>
              <p className="np-muted np-section-lead">
                What came back, what is still out with companies, what needs your OK, and which renewals are about to hit — every week, from your own inbox.
              </p>
              <div className="np-actions"><Link className="np-btn ghost np-btn-icon" to={next}>Set my digest day <ArrowRight size={15} /></Link></div>
            </Reveal>
          </div>
        </section>

        <section className="np-section" id="money">
          <Reveal>
            <div className="np-kicker">Money saved</div>
            <h2 className="np-section-title">Buckets stay separate. We never add them together.</h2>
            <p className="np-muted np-section-lead">The number on your home is verified recovered this year. Potential, pending, confirmed, and avoided renewals are labeled on their own.</p>
          </Reveal>
          <motion.div className="np-grid-4" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            {BUCKETS.map((bucket) => (
              <motion.div variants={fadeUp} className="np-card np-lift" id={bucket.id} key={bucket.id}>
                <span className={`np-tag ${bucket.tone}`}>{bucket.tag}</span>
                <h3 style={{ marginTop: 12 }}>{bucket.title}</h3>
                <p className="np-muted">{bucket.blurb}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <section className="np-section" id="safety">
          <Reveal><div className="np-kicker">Safety</div><h2 className="np-section-title">AI may draft. You still decide. The bank still has to match.</h2></Reveal>
          <motion.div className="np-grid-3" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            {SAFETY.map(({ id, icon: Icon, title, blurb }) => (
              <motion.div variants={fadeUp} className="np-card np-lift" id={id} key={id}>
                <div className="np-icon-box"><Icon size={18} /></div>
                <h3>{title}</h3>
                <p className="np-muted">{blurb}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        <section className="np-section" id="faq">
          <Reveal><div className="np-kicker">Questions</div><h2 className="np-section-title">Straight answers</h2></Reveal>
          <Reveal className="np-faq-v2">
            {FAQ.map(([q, a]) => (
              <details key={q}><summary>{q}</summary><p className="np-muted">{a}</p></details>
            ))}
          </Reveal>
        </section>

        <Reveal className="np-cta-band" style={{ marginBottom: 40 }}>
          <Glow />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="np-kicker">Ready when you are</div>
            <h2>Start with one real email. We take it from there.</h2>
            <p className="np-muted" style={{ maxWidth: '40em', margin: '0 auto 18px' }}>Connect Gmail for the full autopilot, or paste a single refund email to see the tracker in action.</p>
            <div className="np-actions" style={{ justifyContent: 'center' }}>
              <Link className="np-btn gmail np-btn-lg np-btn-icon" to={connectHref}><GmailLogo size={18} /> Connect Gmail</Link>
              <Link className="np-btn ghost np-btn-lg np-btn-icon" to={next}><ClipboardPaste size={16} /> Paste an email</Link>
            </div>
          </div>
        </Reveal>

        <footer className="np-footer">
          <div className="np-footer-grid">
            <div>
              <BrandMark kicker="Get your money back" />
              <p className="np-muted" style={{ marginTop: 10 }}>You approve every send.</p>
            </div>
            <div>
              <div className="np-kicker">Product</div>
              <a href="#refund">Refund never arrived</a>
              <a href="#renewal">Stop a renewal</a>
              <a href="#delivery">Late delivery credit</a>
              <a href="#tracker">Recovery tracker</a>
              <a href="#digest">Weekly digest</a>
            </div>
            <div>
              <div className="np-kicker">How we work</div>
              <a href="#recoveries">What we recover</a>
              <a href="#how">How it works</a>
              <a href="#money">Money saved</a>
              <a href="#safety">Safety</a>
              <a href="#faq">Questions</a>
            </div>
            <div>
              <div className="np-kicker">Account</div>
              {loggedIn ? (
                <>
                  <Link to="/app">Home</Link>
                  <Link to="/ledger">Money saved</Link>
                  <Link to="/settings">Settings</Link>
                </>
              ) : (
                <>
                  <Link to="/auth?mode=login&next=/onboarding">Sign in</Link>
                  <Link to="/auth?mode=register&next=/onboarding">Sign up</Link>
                </>
              )}
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
